/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Commit dialog component
 */

import React from 'react';
import { GitCommit, Loader } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';

interface CommitDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  commitMessage: string;
  onCommitMessageChange: (message: string) => void;
  selectedFilesCount: number;
  onCommit: () => void;
  committing: boolean;
  generatingMessage?: boolean;
}

export function CommitDialog({
  isOpen,
  onOpenChange,
  commitMessage,
  onCommitMessageChange,
  selectedFilesCount,
  onCommit,
  committing,
  generatingMessage = false
}: CommitDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Commit Changes</AlertDialogTitle>
          <AlertDialogDescription>
            {selectedFilesCount > 0
              ? `Commit ${selectedFilesCount} selected file(s)`
              : 'Commit all changes'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="my-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Commit Message *
            {generatingMessage && (
              <span className="ml-2 text-xs text-blue-600 font-normal">
                <Loader size={12} className="inline animate-spin mr-1" />
                Generating...
              </span>
            )}
          </label>
          <textarea
            value={commitMessage}
            onChange={(e) => onCommitMessageChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows={6}
            placeholder={generatingMessage ? "Generating commit message..." : "Enter commit message..."}
            disabled={generatingMessage}
          />
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={committing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onCommit();
            }}
            disabled={!commitMessage.trim() || committing}
          >
            {committing ? (
              <>
                <Loader size={14} className="animate-spin mr-2" />
                Committing...
              </>
            ) : (
              <>
                <GitCommit size={14} className="mr-2" />
                Commit
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

